import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarAPI, ordersAPI, tasksAPI, customersAPI, formSchemaAPI, materialsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ShoppingCart,
  CheckSquare,
  Clock,
  Banknote,
  AlertTriangle,
  ArrowUpRight,
  Filter,
  LayoutGrid,
  Rows3,
  X,
  CreditCard,
  Wallet,
  FileText,
  User,
  Phone,
  Mail,
  Edit,
  Loader2,
  ArrowLeft,
  Smartphone,
  Monitor,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  Paperclip,
  Download,
  Package,
  Star, Heart, MapPin, Tag, Bookmark, Flag, Bell, Truck, Gift,
  Scissors, Palette, Cake, Coffee, Home, Building, Briefcase,
  Shield, Zap, Music, Camera, Globe, Key, Award, Crown, Diamond,
  Flame, Leaf, Sun, Moon, Cloud, Umbrella, Anchor, Compass, Sparkles,
} from 'lucide-react';

const SECTION_ICONS_MAP = {
  'package': Package, 'shopping-cart': ShoppingCart, 'user': User, 'star': Star,
  'heart': Heart, 'map-pin': MapPin, 'phone': Phone, 'mail': Mail,
  'file-text': FileText, 'tag': Tag, 'bookmark': Bookmark, 'flag': Flag,
  'bell': Bell, 'truck': Truck, 'gift': Gift, 'credit-card': CreditCard,
  'scissors': Scissors, 'palette': Palette, 'cake': Cake, 'coffee': Coffee,
  'home': Home, 'building': Building, 'briefcase': Briefcase, 'shield': Shield,
  'zap': Zap, 'music': Music, 'camera': Camera, 'globe': Globe,
  'key': Key, 'award': Award, 'crown': Crown, 'diamond': Diamond,
  'flame': Flame, 'leaf': Leaf, 'sun': Sun, 'moon': Moon,
  'cloud': Cloud, 'umbrella': Umbrella, 'anchor': Anchor, 'compass': Compass,
  'calendar': CalendarDays, 'clock': Clock, 'sparkles': Sparkles,
};
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// ==================== CONSTANTS ====================

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const MONTH_NAMES_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const DAY_NAMES_FULL = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

const STATUS_LABELS = {
  new: 'Новый',
  in_progress: 'В работе',
  completed: 'Завершён',
  cancelled: 'Отменён',
  pending: 'Ожидает',
  done: 'Выполнено',
};

const ORDER_STATUSES = {
  new: { label: 'Новый' },
  in_progress: { label: 'В работе' },
  completed: { label: 'Завершён' },
  cancelled: { label: 'Отменён' },
};

const TASK_STATUSES = {
  pending: { label: 'Ожидает' },
  in_progress: { label: 'В работе' },
  completed: { label: 'Выполнено' },
};

const TASK_PRIORITIES = {
  high: { label: 'Высокий' },
  medium: { label: 'Средний' },
  low: { label: 'Низкий' },
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  pending: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
};

const PRIORITY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500',
};

const PRIORITY_DOT_COLORS = {
  high: 'border-red-400 dark:border-red-500',
  medium: 'border-amber-400 dark:border-amber-500',
  low: 'border-emerald-400 dark:border-emerald-500',
};

const PRIORITY_LABELS = {
  high: 'Высокий',
  medium: 'Средний',
  low: 'Низкий',
};

// ==================== HELPERS ====================

function formatCurrency(amount) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDateFull(dk) {
  if (!dk) return '';
  const [y, m, d] = dk.split('-').map(Number);
  return `${d} ${MONTH_NAMES_GENITIVE[m - 1]} ${y}`;
}

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startDay = firstDay.getDay() - 1;
  if (startDay < 0) startDay = 6;
  const daysInMonth = lastDay.getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const days = [];

  for (let i = startDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    days.push({ day: d, month: pm, year: py, isCurrentMonth: false, dateKey: `${py}-${String(pm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, month, year, isCurrentMonth: true, dateKey: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  const totalSlots = Math.ceil(days.length / 7) * 7;
  for (let d = 1; d <= totalSlots - days.length; d++) {
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    days.push({ day: d, month: nm, year: ny, isCurrentMonth: false, dateKey: `${ny}-${String(nm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  return days;
}

function getWeekDays(baseDate) {
  const d = new Date(baseDate);
  const dayOfWeek = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((dayOfWeek + 6) % 7));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push({
      day: day.getDate(),
      month: day.getMonth(),
      year: day.getFullYear(),
      dateObj: new Date(day),
      isCurrentMonth: day.getMonth() === baseDate.getMonth(),
      dateKey: dateKey(day),
    });
  }
  return days;
}

function getWeekRange(baseDate) {
  const d = new Date(baseDate);
  const dayOfWeek = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((dayOfWeek + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday, end: sunday };
}

// ==================== COMPONENTS ====================

function EventCard({ event, isTask, isOverdue, onClick }) {
  if (isTask) {
    return (
      <div
        onClick={onClick}
        className={cn(
          "rounded-md p-2 cursor-pointer transition-all hover:shadow-sm border-l-[3px] group",
          isOverdue
            ? "bg-red-50 dark:bg-red-950/30 border-l-red-500"
            : event.status === 'completed'
              ? "bg-emerald-50 dark:bg-emerald-950/20 border-l-emerald-400"
              : `bg-purple-50 dark:bg-purple-950/20 ${PRIORITY_DOT_COLORS[event.priority] || 'border-l-purple-400'}`
        )}
      >
        <div className="flex items-start justify-between gap-1.5">
          <div className="min-w-0 flex-1">
            <p className={cn(
              "text-xs font-medium leading-tight truncate",
              event.status === 'completed' && "line-through text-muted-foreground"
            )}>
              {event.title}
            </p>
            {event.subtitle && (
              <p className="text-[10px] text-muted-foreground truncate mt-0.5">{event.subtitle}</p>
            )}
          </div>
          <div className={cn("w-1.5 h-1.5 rounded-full shrink-0 mt-1", PRIORITY_COLORS[event.priority])} />
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="rounded-md p-2 bg-blue-50 dark:bg-blue-950/20 border-l-[3px] border-l-blue-500 cursor-pointer transition-all hover:shadow-sm group"
    >
      <div className="flex items-start justify-between gap-1.5">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium leading-tight truncate">{event.title}</p>
          {event.subtitle && (
            <p className="text-[10px] text-muted-foreground truncate mt-0.5">{event.subtitle}</p>
          )}
        </div>
        {event.total > 0 && (
          <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 shrink-0 whitespace-nowrap">
            {formatCurrency(event.total)}
          </span>
        )}
      </div>
    </div>
  );
}

// ==================== ORDER DETAIL SHEET (CALENDAR) ====================

function CalendarOrderDetail({ orderId, onClose }) {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [formSchema, setFormSchema] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await ordersAPI.getOne(orderId);
        const o = res.data;
        setOrder(o);
        if (o.customer_id) {
          try { const c = await customersAPI.getOne(o.customer_id); setCustomer(c.data); } catch {}
        }
        if (o.form_schema_id) {
          try { const f = await formSchemaAPI.getOne(o.form_schema_id); setFormSchema(f.data); } catch {}
        }
      } catch { }
      setLoading(false);
    })();
  }, [orderId]);

  const getFieldLabel = (fieldId) => {
    if (!formSchema?.fields) return fieldId;
    const field = formSchema.fields.find(f => f.id === fieldId);
    return field?.label || fieldId;
  };

  const formatFieldValue = (fieldId, value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
    // Handle file objects {id, url, name}
    if (typeof value === 'object' && !Array.isArray(value) && value.name) {
      return value.name;
    }
    const field = formSchema?.fields?.find(f => f.id === fieldId);
    if (Array.isArray(value)) {
      // File array
      if (field?.type === 'file') return `${value.length} файл(ов)`;
      // Image gallery
      if (value.length > 0 && typeof value[0] === 'object') return `${value.length} фото`;
      return value.join(', ');
    }
    // Check if this field is a date type
    if (field?.type === 'date' && value) {
      const str = String(value);
      let date;
      if (str.length === 10 && str.includes('-') && !str.includes('T')) {
        const [y, m, d] = str.split('-').map(Number);
        date = new Date(y, m - 1, d);
      } else if (str.includes('T')) {
        const dt = new Date(str);
        if (dt.getUTCHours() >= 12) {
          const nextDay = new Date(dt);
          nextDay.setUTCDate(nextDay.getUTCDate() + 1);
          date = new Date(nextDay.getUTCFullYear(), nextDay.getUTCMonth(), nextDay.getUTCDate());
        } else {
          date = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
        }
      } else {
        date = new Date(str);
      }
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return String(value);
  };

  if (!orderId) return null;

  const statusInfo = order ? (ORDER_STATUSES[order.status] || {}) : {};
  const remaining = order ? (order.total || 0) - (order.prepayment || 0) : 0;

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !order ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <p className="text-muted-foreground">Заказ не найден</p>
            <Button variant="outline" size="sm" onClick={onClose}>Закрыть</Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-6">
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
              <div className="flex items-start gap-4 mt-2">
                <div className="h-14 w-14 rounded-xl bg-primary/20 flex items-center justify-center border-2 border-background shadow-sm">
                  <ShoppingCart className="h-7 w-7 text-primary" />
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <h2 className="text-lg font-bold">Заказ {order.order_number_display || `№${order.order_number}`}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{formatDateTime(order.created_at)}</p>
                  <Badge variant={order.status === 'completed' ? 'default' : 'secondary'} className="mt-2">
                    {statusInfo.label || order.status}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="px-6 pt-6 pb-8">
              {/* Price Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-4">
                  <Banknote className="absolute -right-2 -bottom-2 h-12 w-12 text-emerald-500/10" />
                  <div className="relative">
                    <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Сумма</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(order.total || 0)}</p>
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 p-4">
                  <CreditCard className="absolute -right-2 -bottom-2 h-12 w-12 text-blue-500/10" />
                  <div className="relative">
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">Предоплата</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(order.prepayment || 0)}</p>
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1 relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20 p-4">
                  <Wallet className="absolute -right-2 -bottom-2 h-12 w-12 text-orange-500/10" />
                  <div className="relative">
                    <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide">К оплате</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(remaining)}</p>
                  </div>
                </div>
              </div>

              {/* Customer */}
              {customer && (
                <div className="mt-8">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                    <User className="h-3.5 w-3.5" /> Клиент
                  </h4>
                  <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                    <div className="p-4">
                      <p className="font-medium">{customer.name}</p>
                    </div>
                    {customer.phone && (
                      <a href={`tel:${customer.phone}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors">
                        <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                          <Phone className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="text-sm">{customer.phone}</span>
                      </a>
                    )}
                    {customer.email && (
                      <a href={`mailto:${customer.email}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors">
                        <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="text-sm">{customer.email}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Order Data */}
              {order.data && Object.keys(order.data).length > 0 && (
                <div className="mt-8">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                    <FileText className="h-3.5 w-3.5" /> Данные заказа
                  </h4>
                  <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                    {(formSchema?.fields || []).map((fieldDef) => {
                      // Render section as visual separator
                      if (fieldDef.type === 'section') {
                        const SIcon = fieldDef.icon ? SECTION_ICONS_MAP[fieldDef.icon] : null;
                        return (
                          <div key={fieldDef.id} className="px-4 pt-5 pb-2 bg-muted/50">
                            <div className="flex items-center gap-3">
                              {SIcon && (
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <SIcon className="h-4 w-4 text-primary" />
                                </div>
                              )}
                              <h3 className="text-sm font-semibold text-foreground">{fieldDef.label}</h3>
                              <div className="flex-1 border-t border-border" />
                            </div>
                            {fieldDef.description && (
                              <p className={`text-xs text-muted-foreground mt-1 ${SIcon ? 'ml-11' : ''}`}>{fieldDef.description}</p>
                            )}
                          </div>
                        );
                      }

                      const key = fieldDef.id;
                      const value = order.data[key];
                      if (value === null || value === undefined || value === '') return null;
                      const isImageGallery = fieldDef.type === 'image_gallery';
                      const isFile = fieldDef.type === 'file';
                      return (
                        <div key={key} className="p-4">
                          <p className="text-xs text-muted-foreground mb-1">{fieldDef.label || getFieldLabel(key)}</p>
                          {isImageGallery && Array.isArray(value) && value.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                              {value.map((img) => (
                                <div key={img.id} className="space-y-1.5">
                                  <div className="aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                                    <img
                                      src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                                      alt={img.name}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  {img.description && (
                                    <p className="text-xs text-muted-foreground">{img.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : isFile && (Array.isArray(value) || (typeof value === 'object' && value.url)) ? (
                            <div className="space-y-2 mt-1.5">
                              {(Array.isArray(value) ? value : [value]).map((file) => (
                                <a
                                  key={file.id}
                                  href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/60 transition-colors group max-w-full"
                                >
                                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <Paperclip className="h-4 w-4 text-primary" />
                                  </div>
                                  <span className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                                    {file.name}
                                  </span>
                                  <Download className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-auto" />
                                </a>
                              ))}
                            </div>
                          ) : isFile && typeof value === 'string' && value ? (
                            <div className="inline-flex items-center gap-2 p-2.5 mt-1 rounded-lg border border-border bg-muted/20">
                              <Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="text-sm">{value}</span>
                            </div>
                          ) : (
                            <p className="font-medium">{formatFieldValue(key, value)}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Form Name */}
              {formSchema && (
                <div className="mt-6 p-3 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground">Форма заказа</p>
                  <p className="text-sm font-medium">{formSchema.name}</p>
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex gap-2">
                <Button onClick={() => { onClose(); navigate(`/orders?view=${order.id}`); }} className="flex-1 h-12 font-medium" variant="default">
                  <Edit className="h-4 w-4 mr-2" /> Редактировать
                </Button>
                <Button onClick={onClose} variant="outline" className="h-12 px-4">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Назад
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ==================== TASK DETAIL SHEET (CALENDAR) ====================

function CalendarTaskDetail({ taskId, onClose }) {
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await tasksAPI.getOne(taskId);
        const t = res.data;
        setTask(t);
        // Fetch related order
        if (t.order_id) {
          try {
            const oRes = await ordersAPI.getOne(t.order_id);
            const o = oRes.data;
            setOrder(o);
            // Fetch customer from order
            if (o.customer_id) {
              try { const cRes = await customersAPI.getOne(o.customer_id); setCustomer(cRes.data); } catch {}
            }
          } catch {}
        }
        // Fetch related material
        if (t.material_id) {
          try {
            const mRes = await materialsAPI.getOne(t.material_id);
            setMaterial(mRes.data);
          } catch {}
        }
      } catch { }
      setLoading(false);
    })();
  }, [taskId]);

  if (!taskId) return null;

  const statusInfo = task ? (TASK_STATUSES[task.status] || {}) : {};
  const priorityInfo = task ? (TASK_PRIORITIES[task.priority] || {}) : {};
  const isOverdue = task?.due_date && new Date(task.due_date) < new Date() && task?.status !== 'completed';

  // Get client name from customer or order data
  const getClientName = () => {
    if (!order) return null;
    if (customer?.name) return customer.name;
    if (order.data) {
      const nameFields = ['name', 'client_name', 'customer_name', 'имя', 'клиент', 'ФИО'];
      for (const field of nameFields) {
        if (order.data[field]) return order.data[field];
      }
    }
    return null;
  };

  const clientName = task && order ? getClientName() : null;

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !task ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <p className="text-muted-foreground">Задача не найдена</p>
            <Button variant="outline" size="sm" onClick={onClose}>Закрыть</Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className={cn(
              "px-6 pt-8 pb-6",
              task.status === 'completed'
                ? "bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent"
                : isOverdue
                  ? "bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent"
                  : "bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
            )}>
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
              <div className="flex items-start gap-4 mt-2">
                <div className={cn(
                  "h-14 w-14 rounded-xl flex items-center justify-center border-2 border-background shadow-sm",
                  task.status === 'completed' ? "bg-green-500/20 text-green-600"
                    : isOverdue ? "bg-red-500/20 text-red-600"
                    : "bg-primary/20 text-primary"
                )}>
                  <CheckSquare className="h-7 w-7" />
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <h2 className="text-lg font-bold leading-tight">{task.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{formatDateTime(task.created_at)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={task.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                      {statusInfo.label || task.status}
                    </Badge>
                    <Badge variant={task.priority === 'high' ? 'destructive' : 'outline'} className="text-xs">
                      {priorityInfo.label || task.priority}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pt-6 pb-8">
              {/* Due Date */}
              {task.due_date && (
                <div className={cn(
                  "relative overflow-hidden rounded-xl border p-4 mb-6",
                  isOverdue
                    ? "bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20"
                    : "bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20"
                )}>
                  <Clock className={cn(
                    "absolute -right-2 -bottom-2 h-12 w-12",
                    isOverdue ? "text-red-500/10" : "text-blue-500/10"
                  )} />
                  <div className="relative">
                    <p className={cn(
                      "text-xs font-medium uppercase tracking-wide",
                      isOverdue ? "text-red-600 dark:text-red-400" : "text-blue-600 dark:text-blue-400"
                    )}>
                      {isOverdue ? 'Просрочено' : 'Срок выполнения'}
                    </p>
                    <p className="text-xl font-bold mt-1">{formatDate(task.due_date)}</p>
                  </div>
                </div>
              )}

              {/* Description */}
              {task.description && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Описание</h4>
                  <div className="p-4 rounded-xl bg-muted/50 border border-border">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{task.description}</p>
                  </div>
                </div>
              )}

              {/* Related Order */}
              {order && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                    <ShoppingCart className="h-3.5 w-3.5" /> Связанный заказ
                  </h4>
                  <div
                    className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                    onClick={() => { onClose(); navigate(`/orders?view=${order.id}`); }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-primary">Заказ {order.order_number_display || `№${order.order_number}`} →</p>
                        {clientName && <p className="text-sm text-muted-foreground">{clientName}</p>}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {ORDER_STATUSES[order.status]?.label || order.status}
                      </Badge>
                    </div>
                    {(order.total > 0) && (
                      <p className="text-lg font-bold mt-2">{formatCurrency(order.total)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Related Material */}
              {material && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                    <Package className="h-3.5 w-3.5" /> Связанный материал
                  </h4>
                  <div
                    className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                    onClick={() => { onClose(); navigate(`/materials?view=${material.id}`); }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Package className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{material.name} →</p>
                          {material.sku && <p className="text-xs text-muted-foreground">{material.sku}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn("text-sm font-semibold", material.quantity <= material.min_quantity && "text-destructive")}>
                          {material.quantity} {material.unit}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(material.price)}/{material.unit}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Info */}
              {customer && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                    <User className="h-3.5 w-3.5" /> Клиент
                  </h4>
                  <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                    <div className="p-4">
                      <p className="font-medium">{customer.name}</p>
                    </div>
                    {customer.phone && (
                      <a href={`tel:${customer.phone}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors">
                        <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                          <Phone className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="text-sm">{customer.phone}</span>
                      </a>
                    )}
                    {customer.email && (
                      <a href={`mailto:${customer.email}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors">
                        <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="text-sm">{customer.email}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex gap-2">
                <Button onClick={() => { onClose(); navigate(`/tasks?view=${task.id}`); }} className="flex-1 h-12 font-medium" variant="default">
                  <Edit className="h-4 w-4 mr-2" /> Редактировать
                </Button>
                <Button onClick={onClose} variant="outline" className="h-12 px-4">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Назад
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ==================== MAIN COMPONENT ====================

export default function CalendarPage() {
  const navigate = useNavigate();
  const today = new Date();
  const todayStr = dateKey(today);

  // View and navigation state
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [weekBase, setWeekBase] = useState(new Date(today));

  // Data state
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [eventFilter, setEventFilter] = useState('all'); // 'all' | 'orders' | 'tasks'
  const [statusFilter, setStatusFilter] = useState('all');

  // Sheet
  const [selectedDate, setSelectedDate] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Detail view state (opens order/task detail without leaving calendar)
  const [viewingOrderId, setViewingOrderId] = useState(null);
  const [viewingTaskId, setViewingTaskId] = useState(null);

  // Calendar sync state
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [calendarUrlLoading, setCalendarUrlLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const handleOpenCalendarDialog = async () => {
    setCalendarDialogOpen(true);
    if (calendarUrl) return;
    setCalendarUrlLoading(true);
    try {
      const res = await tasksAPI.getCalendarToken();
      const token = res.data.calendar_token;
      const host = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      setCalendarUrl(`${host}/api/calendar/${token}.ics`);
    } catch {
      // silent
    } finally {
      setCalendarUrlLoading(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(calendarUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  // ==================== DATA FETCHING ====================

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate the full visible date range
      let startDate, endDate;
      
      if (viewMode === 'week') {
        // Week view: use exact week boundaries
        const wDays = getWeekDays(weekBase);
        const firstDay = wDays[0];
        const lastDay = wDays[6];
        startDate = firstDay.dateKey + 'T00:00:00+00:00';
        // End date is day after last day
        const endD = new Date(lastDay.year, lastDay.month, lastDay.day + 1);
        endDate = dateKey(endD) + 'T00:00:00+00:00';
      } else {
        // Month view: include visible days from adjacent months
        const days = getCalendarDays(currentYear, currentMonth);
        const firstVisible = days[0];
        const lastVisible = days[days.length - 1];
        startDate = firstVisible.dateKey + 'T00:00:00+00:00';
        // End date is day after last visible day
        const endD = new Date(lastVisible.year, lastVisible.month, lastVisible.day + 1);
        endDate = dateKey(endD) + 'T00:00:00+00:00';
      }
      
      const res = await calendarAPI.getEvents(currentYear, currentMonth + 1, startDate, endDate);
      setOrders(res.data.orders || []);
      setTasks(res.data.tasks || []);
    } catch (err) {
      console.error('Failed to load calendar events:', err);
      setOrders([]);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth, viewMode, weekBase]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Sync month when navigating weeks
  useEffect(() => {
    if (viewMode === 'week') {
      const m = weekBase.getMonth();
      const y = weekBase.getFullYear();
      if (m !== currentMonth || y !== currentYear) {
        setCurrentMonth(m);
        setCurrentYear(y);
      }
    }
  }, [viewMode, weekBase, currentMonth, currentYear]);

  // ==================== NAVIGATION ====================

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const prevWeek = () => {
    const d = new Date(weekBase);
    d.setDate(d.getDate() - 7);
    setWeekBase(d);
  };

  const nextWeek = () => {
    const d = new Date(weekBase);
    d.setDate(d.getDate() + 7);
    setWeekBase(d);
  };

  const goToToday = () => {
    const t = new Date();
    setCurrentYear(t.getFullYear());
    setCurrentMonth(t.getMonth());
    setWeekBase(new Date(t));
  };

  // ==================== FILTERED DATA ====================

  const filteredOrders = useMemo(() => {
    if (eventFilter === 'tasks') return [];
    let result = orders;
    if (statusFilter !== 'all') {
      result = result.filter(o => o.status === statusFilter);
    }
    return result;
  }, [orders, eventFilter, statusFilter]);

  const filteredTasks = useMemo(() => {
    if (eventFilter === 'orders') return [];
    let result = tasks;
    if (statusFilter !== 'all') {
      result = result.filter(t => t.status === statusFilter);
    }
    return result;
  }, [tasks, eventFilter, statusFilter]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    filteredOrders.forEach(o => {
      if (!map[o.date]) map[o.date] = { orders: [], tasks: [] };
      map[o.date].orders.push(o);
    });
    filteredTasks.forEach(t => {
      if (!map[t.date]) map[t.date] = { orders: [], tasks: [] };
      map[t.date].tasks.push(t);
    });
    return map;
  }, [filteredOrders, filteredTasks]);

  // ==================== COMPUTED DATA ====================

  const calendarDays = useMemo(() => getCalendarDays(currentYear, currentMonth), [currentYear, currentMonth]);
  const weekDays = useMemo(() => getWeekDays(weekBase), [weekBase]);
  const weekRange = useMemo(() => getWeekRange(weekBase), [weekBase]);

  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return { orders: [], tasks: [] };
    return eventsByDate[selectedDate] || { orders: [], tasks: [] };
  }, [selectedDate, eventsByDate]);

  const monthStats = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const totalTasks = filteredTasks.length;
    const totalRevenue = filteredOrders.reduce((sum, o) => sum + (o.total || 0), 0);
    const overdueTasks = filteredTasks.filter(t => {
      if (t.status === 'completed') return false;
      return new Date(t.date) < today;
    }).length;
    return { totalOrders, totalTasks, totalRevenue, overdueTasks };
  }, [filteredOrders, filteredTasks, today]);

  const hasActiveFilters = eventFilter !== 'all' || statusFilter !== 'all';

  // ==================== HANDLERS ====================

  const handleDayClick = (dayInfo) => {
    setSelectedDate(dayInfo.dateKey);
    setSheetOpen(true);
  };

  const clearFilters = () => {
    setEventFilter('all');
    setStatusFilter('all');
  };

  // Format week range header
  const weekHeaderText = useMemo(() => {
    const s = weekRange.start;
    const e = weekRange.end;
    if (s.getMonth() === e.getMonth()) {
      return `${s.getDate()} – ${e.getDate()} ${MONTH_NAMES_GENITIVE[s.getMonth()]} ${s.getFullYear()}`;
    }
    return `${s.getDate()} ${MONTH_NAMES_GENITIVE[s.getMonth()]} – ${e.getDate()} ${MONTH_NAMES_GENITIVE[e.getMonth()]} ${e.getFullYear()}`;
  }, [weekRange]);

  // ==================== RENDER ====================

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Календарь</h1>
          <p className="text-sm text-muted-foreground">Заказы и задачи по дням</p>
        </div>
        {/* Sync & View Toggle */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenCalendarDialog}
            title="Синхронизация календаря"
            className="h-8 gap-1.5"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            <span>Синхронизация</span>
          </Button>
          <div className="flex items-center gap-1.5 bg-muted rounded-lg p-1">
          <Button
            variant={viewMode === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('month')}
            className="h-8 gap-1.5"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Месяц</span>
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('week')}
            className="h-8 gap-1.5"
          >
            <Rows3 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Неделя</span>
          </Button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Фильтры:</span>
        </div>
        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <SelectValue placeholder="Тип событий" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все события</SelectItem>
            <SelectItem value="orders">Только заказы</SelectItem>
            <SelectItem value="tasks">Только задачи</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="new">Новый</SelectItem>
            <SelectItem value="in_progress">В работе</SelectItem>
            <SelectItem value="pending">Ожидает</SelectItem>
            <SelectItem value="completed">Завершён</SelectItem>
            <SelectItem value="cancelled">Отменён</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs text-muted-foreground hover:text-foreground">
            Сбросить
          </Button>
        )}
        {/* Stats summary inline */}
        <div className="ml-auto hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <ShoppingCart className="h-3 w-3 text-blue-500" /> {monthStats.totalOrders}
          </span>
          <span className="flex items-center gap-1">
            <CheckSquare className="h-3 w-3 text-purple-500" /> {monthStats.totalTasks}
          </span>
          <span className="flex items-center gap-1">
            <Banknote className="h-3 w-3 text-emerald-500" /> {formatCurrency(monthStats.totalRevenue)}
          </span>
          {monthStats.overdueTasks > 0 && (
            <span className="flex items-center gap-1 text-red-500">
              <AlertTriangle className="h-3 w-3" /> {monthStats.overdueTasks}
            </span>
          )}
        </div>
      </div>

      {/* Mobile Stats Row */}
      <div className="grid grid-cols-4 gap-2 sm:hidden">
        <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-950/30 rounded-lg px-2 py-1.5">
          <ShoppingCart className="h-3 w-3 text-blue-500" />
          <span className="text-xs font-semibold">{monthStats.totalOrders}</span>
        </div>
        <div className="flex items-center gap-1.5 bg-purple-50 dark:bg-purple-950/30 rounded-lg px-2 py-1.5">
          <CheckSquare className="h-3 w-3 text-purple-500" />
          <span className="text-xs font-semibold">{monthStats.totalTasks}</span>
        </div>
        <div className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-2 py-1.5">
          <Banknote className="h-3 w-3 text-emerald-500" />
          <span className="text-xs font-semibold truncate">{formatCurrency(monthStats.totalRevenue)}</span>
        </div>
        <div className={cn(
          "flex items-center gap-1.5 rounded-lg px-2 py-1.5",
          monthStats.overdueTasks > 0 ? "bg-red-50 dark:bg-red-950/30" : "bg-gray-50 dark:bg-gray-800/50"
        )}>
          <AlertTriangle className={cn("h-3 w-3", monthStats.overdueTasks > 0 ? "text-red-500" : "text-gray-400")} />
          <span className="text-xs font-semibold">{monthStats.overdueTasks}</span>
        </div>
      </div>

      {/* Calendar Card */}
      <Card>
        <CardContent className="p-3 sm:p-6">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" size="icon" onClick={viewMode === 'month' ? prevMonth : prevWeek} className="h-9 w-9">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-base sm:text-xl font-semibold min-w-[160px] sm:min-w-[260px] text-center">
                {viewMode === 'month'
                  ? `${MONTH_NAMES[currentMonth]} ${currentYear}`
                  : weekHeaderText
                }
              </h2>
              <Button variant="outline" size="icon" onClick={viewMode === 'month' ? nextMonth : nextWeek} className="h-9 w-9">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday} className="h-8 text-xs gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Сегодня</span>
            </Button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_NAMES.map((day, i) => (
              <div key={day} className={cn(
                "text-center text-xs font-medium py-2",
                viewMode === 'week' && weekDays[i]?.dateKey === todayStr
                  ? "text-primary font-bold"
                  : "text-muted-foreground"
              )}>
                {viewMode === 'week' ? (
                  <div>
                    <div>{day}</div>
                    <div className={cn(
                      "text-sm mt-0.5",
                      weekDays[i]?.dateKey === todayStr
                        ? "bg-primary text-primary-foreground rounded-full w-7 h-7 inline-flex items-center justify-center font-bold"
                        : "text-foreground"
                    )}>
                      {weekDays[i]?.day}
                    </div>
                  </div>
                ) : day}
              </div>
            ))}
          </div>

          {/* ==================== MONTH VIEW ==================== */}
          {viewMode === 'month' && (
            loading ? (
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {Array.from({ length: 35 }).map((_, i) => (
                  <div key={i} className="bg-card aspect-square sm:aspect-auto sm:min-h-[100px] animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {calendarDays.map((dayInfo, idx) => {
                  const events = eventsByDate[dayInfo.dateKey];
                  const isToday = dayInfo.dateKey === todayStr;
                  const hasOrders = events?.orders?.length > 0;
                  const hasTasks = events?.tasks?.length > 0;
                  const hasOverdue = events?.tasks?.some(t =>
                    t.status !== 'completed' && new Date(t.date) < today
                  );

                  return (
                    <button
                      key={idx}
                      onClick={() => handleDayClick(dayInfo)}
                      className={cn(
                        "bg-card relative text-left transition-colors group",
                        "aspect-square sm:aspect-auto sm:min-h-[100px] p-1 sm:p-2",
                        "hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-inset",
                        !dayInfo.isCurrentMonth && "opacity-40",
                        isToday && "ring-2 ring-primary/50 ring-inset"
                      )}
                    >
                      <div className={cn(
                        "text-xs sm:text-sm font-medium mb-0.5 sm:mb-1",
                        isToday && "text-primary font-bold",
                        !dayInfo.isCurrentMonth && "text-muted-foreground"
                      )}>
                        <span className={cn(
                          "inline-flex items-center justify-center",
                          isToday && "bg-primary text-primary-foreground rounded-full w-6 h-6 sm:w-7 sm:h-7 text-xs sm:text-sm"
                        )}>
                          {dayInfo.day}
                        </span>
                      </div>

                      {/* Mobile dots */}
                      <div className="flex items-center gap-0.5 sm:hidden">
                        {hasOrders && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        {hasTasks && !hasOverdue && <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
                        {hasOverdue && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                      </div>

                      {/* Desktop mini cards */}
                      <div className="hidden sm:flex flex-col gap-0.5 overflow-hidden">
                        {events?.orders?.slice(0, 2).map((order, oi) => (
                          <div key={`o-${oi}`} className="text-[10px] leading-tight truncate px-1 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                            {order.title}
                          </div>
                        ))}
                        {events?.tasks?.slice(0, 2).map((task, ti) => (
                          <div key={`t-${ti}`} className={cn(
                            "text-[10px] leading-tight truncate px-1 py-0.5 rounded",
                            task.status !== 'completed' && new Date(task.date) < today
                              ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                              : "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                          )}>
                            {task.title}
                          </div>
                        ))}
                        {events && (events.orders.length + events.tasks.length) > 4 && (
                          <div className="text-[10px] text-muted-foreground pl-1">
                            +{events.orders.length + events.tasks.length - 4}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          )}

          {/* ==================== WEEK VIEW ==================== */}
          {viewMode === 'week' && (
            loading ? (
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="bg-muted/30 rounded-lg min-h-[300px] animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-px sm:gap-2">
                {weekDays.map((dayInfo, idx) => {
                  const events = eventsByDate[dayInfo.dateKey];
                  const isToday = dayInfo.dateKey === todayStr;
                  const dayOrders = events?.orders || [];
                  const dayTasks = events?.tasks || [];
                  const totalEvents = dayOrders.length + dayTasks.length;

                  return (
                    <div
                      key={idx}
                      className={cn(
                        "rounded-lg border min-h-[200px] sm:min-h-[350px] flex flex-col transition-colors",
                        isToday
                          ? "border-primary/50 bg-primary/[0.02] dark:bg-primary/[0.03]"
                          : "border-border bg-card",
                        (dayInfo.dateObj?.getDay() === 0 || dayInfo.dateObj?.getDay() === 6) && "bg-muted/20"
                      )}
                    >
                      {/* Day header in week view */}
                      <button
                        onClick={() => handleDayClick(dayInfo)}
                        className="p-1.5 sm:p-2 text-center border-b border-border/50 hover:bg-muted/30 transition-colors"
                      >
                        <div className="sm:hidden text-[10px] text-muted-foreground">
                          {totalEvents > 0 && (
                            <span className="font-medium text-foreground">{totalEvents}</span>
                          )}
                        </div>
                      </button>

                      {/* Events list */}
                      <ScrollArea className="flex-1 p-1 sm:p-1.5">
                        <div className="space-y-1">
                          {dayOrders.map((order) => (
                            <EventCard
                              key={order.id}
                              event={order}
                              isTask={false}
                              onClick={() => {
                                setSelectedDate(dayInfo.dateKey);
                                setSheetOpen(true);
                              }}
                            />
                          ))}
                          {dayTasks.map((task) => {
                            const isOverdue = task.status !== 'completed' && new Date(task.date) < today;
                            return (
                              <EventCard
                                key={task.id}
                                event={task}
                                isTask={true}
                                isOverdue={isOverdue}
                                onClick={() => {
                                  setSelectedDate(dayInfo.dateKey);
                                  setSheetOpen(true);
                                }}
                              />
                            );
                          })}
                          {totalEvents === 0 && (
                            <div className="hidden sm:flex items-center justify-center h-20 text-[10px] text-muted-foreground/40">
                              Нет событий
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 sm:gap-6 mt-4 justify-center">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              Заказы
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
              Задачи
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              Просрочено
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Day Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md p-0">
          <SheetHeader className="p-4 sm:p-6 pb-0">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-lg">{formatDateFull(selectedDate)}</SheetTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedDateEvents.orders.length + selectedDateEvents.tasks.length === 0
                    ? 'Нет событий'
                    : `${selectedDateEvents.orders.length + selectedDateEvents.tasks.length} событий`
                  }
                </p>
              </div>
            </div>
          </SheetHeader>

          <Separator className="my-4" />

          <ScrollArea className="h-[calc(100vh-140px)] px-4 sm:px-6 pb-6">
            {selectedDateEvents.orders.length === 0 && selectedDateEvents.tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <CalendarDays className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <p className="text-sm font-medium text-muted-foreground">Нет событий</p>
                <p className="text-xs text-muted-foreground/70 mt-1">В этот день нет заказов и задач</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Orders */}
                {selectedDateEvents.orders.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="h-6 w-6 rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <ShoppingCart className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-sm font-semibold">
                        Заказы ({selectedDateEvents.orders.length})
                      </h3>
                    </div>
                    <div className="flex flex-col gap-3">
                      {selectedDateEvents.orders.map(order => (
                        <Card
                          key={order.id}
                          className="cursor-pointer hover:bg-muted/50 transition-colors group shadow-sm"
                          onClick={() => { setSheetOpen(false); setViewingOrderId(order.id); }}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium">{order.title}</p>
                                  <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                {order.subtitle && (
                                  <p className="text-xs text-muted-foreground truncate mt-0.5">{order.subtitle}</p>
                                )}
                                {order.customer_name && (
                                  <p className="text-xs text-muted-foreground mt-0.5">Клиент: {order.customer_name}</p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1.5 shrink-0">
                                <Badge className={cn("text-[10px] px-1.5 py-0", STATUS_COLORS[order.status])}>
                                  {STATUS_LABELS[order.status] || order.status}
                                </Badge>
                                {order.total > 0 && (
                                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                    {formatCurrency(order.total)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tasks */}
                {selectedDateEvents.tasks.length > 0 && (
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="h-6 w-6 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                        <CheckSquare className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <h3 className="text-sm font-semibold">
                        Задачи ({selectedDateEvents.tasks.length})
                      </h3>
                    </div>
                    <div className="flex flex-col gap-3">
                      {selectedDateEvents.tasks.map(task => {
                        const isOverdue = task.status !== 'completed' && new Date(task.date) < today;
                        return (
                          <Card
                            key={task.id}
                            className={cn(
                              "cursor-pointer hover:bg-muted/50 transition-colors group shadow-sm",
                              isOverdue && "border-red-200 dark:border-red-800/50"
                            )}
                            onClick={() => { setSheetOpen(false); setViewingTaskId(task.id); }}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className={cn("w-2 h-2 rounded-full shrink-0", PRIORITY_COLORS[task.priority])} />
                                    <p className={cn(
                                      "text-sm font-medium",
                                      task.status === 'completed' && "line-through text-muted-foreground"
                                    )}>
                                      {task.title}
                                    </p>
                                    <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                  </div>
                                  {task.subtitle && (
                                    <p className="text-xs text-muted-foreground truncate mt-0.5 ml-4">{task.subtitle}</p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1.5 shrink-0">
                                  <Badge className={cn("text-[10px] px-1.5 py-0", STATUS_COLORS[task.status])}>
                                    {STATUS_LABELS[task.status] || task.status}
                                  </Badge>
                                  {isOverdue && (
                                    <span className="text-[10px] text-red-500 font-medium flex items-center gap-0.5">
                                      <Clock className="h-3 w-3" />
                                      Просрочено
                                    </span>
                                  )}
                                  <span className="text-[10px] text-muted-foreground">
                                    {PRIORITY_LABELS[task.priority]}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Order Detail Sheet (inline, no navigation) */}
      {viewingOrderId && (
        <CalendarOrderDetail
          orderId={viewingOrderId}
          onClose={() => setViewingOrderId(null)}
        />
      )}

      {/* Task Detail Sheet (inline, no navigation) */}
      {viewingTaskId && (
        <CalendarTaskDetail
          taskId={viewingTaskId}
          onClose={() => setViewingTaskId(null)}
        />
      )}

      {/* Calendar Sync Dialog */}
      <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Синхронизация календаря
            </DialogTitle>
            <DialogDescription>
              Подпишитесь на календарь — задачи и заказы будут автоматически обновляться в вашем приложении.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Link section */}
            <div className="rounded-xl border border-border bg-muted p-4 space-y-3">
              <p className="text-sm font-semibold">Ссылка для подписки</p>
              {calendarUrlLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Генерация ссылки...
                </div>
              ) : calendarUrl ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground font-mono break-all">
                    {calendarUrl}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={handleCopyUrl}>
                      {urlCopied ? <Check className="h-4 w-4 mr-1.5 text-green-500" /> : <Copy className="h-4 w-4 mr-1.5" />}
                      {urlCopied ? 'Скопировано!' : 'Скопировать'}
                    </Button>
                    <a
                      href={calendarUrl.replace(/^https?:\/\//, 'webcal://')}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Открыть
                    </a>
                  </div>
                </div>
              ) : null}
            </div>

            {/* iPhone */}
            <div className="rounded-xl border border-border bg-muted p-4 space-y-2">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                iPhone / iPad
              </p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Нажмите <span className="font-medium text-foreground">«Открыть»</span> или скопируйте ссылку и откройте в Safari</li>
                <li>Нажмите <span className="font-medium text-foreground">«Подписаться»</span> в появившемся окне</li>
              </ol>
            </div>

            {/* Android / Google Calendar */}
            <div className="rounded-xl border border-border bg-muted p-4 space-y-2">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                Android / Google Календарь
              </p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Откройте <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">calendar.google.com</a></li>
                <li><span className="font-medium text-foreground">Другие календари → +</span> → <span className="font-medium text-foreground">По URL</span></li>
                <li>Вставьте скопированную ссылку и нажмите <span className="font-medium text-foreground">«Добавить»</span></li>
              </ol>
            </div>

            {/* Desktop: Outlook / macOS */}
            <div className="rounded-xl border border-border bg-muted p-4 space-y-2">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Monitor className="h-4 w-4 text-muted-foreground" />
                Outlook / macOS Календарь
              </p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Нажмите <span className="font-medium text-foreground">«Открыть»</span> — приложение предложит подписаться</li>
                <li>Или скопируйте ссылку и добавьте как интернет-календарь</li>
              </ol>
            </div>

            <p className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2">
              Данные обновляются автоматически. Ссылку можно переслать себе в Telegram или Email.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
